import { BackendHttpService } from '../../../services/backendHttpService';
import { QualificationSummary, QualificationSummarySelectCv } from '../models/qualificationSummary.model';

export class QualificationSummaryService extends BackendHttpService {
  constructor() {
    super('summary');
  }

  /**
   * Get summaries by person ID
   * @param personId The person ID to get summaries for
   * @returns Promise with array of summaries
   */
  async getSummariesByPersonId(personId: number): Promise<QualificationSummary[]> {
    try {
      const response = await this.get<any[]>(`/byPersonId/${personId}`);
      return response.map(item => ({ ...item, personId: item.userId ?? 0 }));
    } catch (error) {
      console.error(`Error fetching summaries for person ${personId}:`, error);
      throw error;
    }
  }

  /**
   * Add a new summary
   * @param summary The summary to add
   * @returns Promise with the created summary
   */
  async addSummary(summary: QualificationSummary): Promise<QualificationSummary> {
    try {
      const { personId, ...rest } = summary;
      const payload = { ...rest, userId: personId }; // backend expects 'userId' JSON key (holds personId) -expected by CreateChildEntityRequest
      const response = await this.post<typeof payload, any>(payload);
      return { ...response, personId: response.userId ?? 0 };
    } catch (error) {
      console.error('Error adding summary:', error);
      throw error;
    }
  }

  /**
   * Update an existing summary
   * @param summary The summary to update
   * @returns Promise with the updated summary
   */
  async updateSummary(summary: QualificationSummary): Promise<QualificationSummary> {
    try {
      const { personId, ...rest } = summary;
      const payload = { ...rest, userId: personId }; // backend expects 'userId' JSON key (holds personId) -expected by CreateChildEntityRequest
      const response = await this.put<typeof payload, any>(payload, `/${summary.id}`);
      return { ...response, personId: response.userId ?? 0 };
    } catch (error) {
      console.error(`Error updating summary with id ${summary.id}:`, error);
      throw error;
    }
  }

  /**
   * Delete a summary
   * @param summaryId The ID of the summary to delete
   * @returns Promise that resolves when deletion is complete
   */
  async deleteSummary(summaryId: number): Promise<void> {
    try {
      await this.delete(`/${summaryId}`);
    } catch (error) {
      console.error(`Error deleting summary with id ${summaryId}:`, error);
      throw error;
    }
  }

  /**
   * Select a summary for CV export by providing ID and personId directly
   * @param id The ID of the summary to select
   * @param personId The person ID associated with the summary
   * @returns Promise with the updated summary selection
   */
  async selectSummaryForCV(id: number, personId: number): Promise<QualificationSummarySelectCv> {
    try {
      const payload = { id, userId: personId }; // backend SelectSummaryForCvCommand inherits UserId from CreateChildEntityRequest
      return await this.put<typeof payload, QualificationSummarySelectCv>(payload, '/selectForCv');
    } catch (error) {
      console.error(`Error selecting summary ${id} for CV export:`, error);
      throw error;
    }
  }

  // CvDataService implementation
  async getDataByPersonID(personId: number): Promise<QualificationSummary[]> {
    return this.getSummariesByPersonId(personId);
  }

  async addData(data: QualificationSummary): Promise<QualificationSummary> {
    return this.addSummary(data);
  }

  async updateData(data: QualificationSummary): Promise<QualificationSummary> {
    return this.updateSummary(data);
  }

  async deleteData(data: QualificationSummary): Promise<void> {
    return this.deleteSummary(data.id);
  }
} 